import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import MetaTags from '../../components/SEO/MetaTags';
import { pageConfigs, generateBlogPostSchema } from '../../utils/seo';

const FutureOfDocumentation: React.FC = () => {
  // Generate structured data for the blog post
  const blogSchema = generateBlogPostSchema({
    title: "The Future of Documentation: AI That Follows the Visit, Not the Form",
    description: "Discover how AI-powered documentation can follow your PT sessions naturally instead of forcing care into rigid forms. Built with clinicians for real practice.",
    datePublished: "2025-12-16",
    dateModified: "2025-12-16",
    author: "Christina Rama",
    url: "/blogs/ai-documentation-follows-visit-not-form",
    image: "/blog/blog-10.png"
  });

  return (
    <>
      <MetaTags
        title={pageConfigs.blogFutureOfDocumentation.title}
        description={pageConfigs.blogFutureOfDocumentation.description}
        keywords={pageConfigs.blogFutureOfDocumentation.keywords}
        canonical={pageConfigs.blogFutureOfDocumentation.canonical}
        ogImage="/blog/blog-10.png"
        ogType="article"
        schemaData={blogSchema}
        additionalMeta={[
          { property: "article:author", content: "Christina Rama" },
          { property: "article:published_time", content: "2025-12-16T00:00:00Z" },
          { property: "article:modified_time", content: "2025-12-16T00:00:00Z" },
          { property: "article:section", content: "Healthcare Technology" },
          { property: "article:tag", content: "AI Documentation" },
          { property: "article:tag", content: "Physical Therapy Documentation" },
          { property: "article:tag", content: "Clinical Workflow" },
          { property: "article:tag", content: "Healthcare AI" },
          { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" }
        ]}
      />
      
      <div className="bg-white">
        {/* Hero section with title */}
        <div className="bg-darkGreen text-white pt-36 pb-8 mb-16 relative overflow-hidden">
          <div className="relative z-10">
            <div className="max-w-3xl mx-auto text-center px-4">
              <h4 className="text-emerald-400 font-medium mb-2">Blog Post</h4>
              <h1 className="text-4xl lg:text-5xl font-bold mb-4 font-productSans">
                The Future of Documentation: AI That Follows the Visit, Not the Form
              </h1>
              <p className="text-emerald-100 text-lg">
                AI That Follows The Visit, Not The Form
              </p>
              <p className="text-emerald-200 text-sm mt-4">
                By Christina Rama • December 16, 2025
              </p>
            </div>
          </div>
        </div>

        {/* Blog content */}
        <div className="max-w-3xl mx-auto px-4 pb-16">
          {/* Back button at top */}
          <div className="mb-8">
            <Link 
              to="/blogs" 
              className="inline-flex items-center px-6 py-3 border border-gray-800 rounded-full text-gray-800 hover:bg-darkGreen hover:text-white transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blogs
            </Link>
          </div>

          {/* Blog image */}
          <div className="mb-12">
            <img
              src="/blog/blog-10.png"
              alt="Physical therapist working with patient while AI documentation captures visit in real-time"
              className="w-full rounded-lg shadow-lg"
            />
          </div>

          {/* Article content */}
          <article className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              Healthcare has spent years trying to speed up documentation. Templates became tighter, shortcuts became smarter, and voice tools became more accurate. Yet the same problem remains. Charting still takes too long because the underlying structure has not changed.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              Most documentation tools expect a visit to follow a predetermined path. Real care does not unfold that way. Clinicians make decisions in the moment based on what the patient shows, what changes, and what needs to be taught. When the tool cannot follow that rhythm, clinicians end up rebuilding the visit after it is over.
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              AI offers a path forward, but it can only close this gap when the people who understand how a session actually unfolds guide how the system is built. Clinicians hold the insight that gives each visit its structure, and meaningful tools must be shaped by that perspective.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Where Traditional PT Documentation Breaks Down</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              PTs move through a session with a clear sense of what happened and why, yet documentation often feels fragmented. Instead of recording information as it naturally appears, clinicians spend time searching for the right field, sorting through menus, or repeating the same detail in multiple places. The work shifts from capturing the visit to operating the tool.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              These interruptions break the pace of care. A therapist may be watching how a patient responds to a cue or preparing to teach a movement, but the structure of the note pulls attention back to the computer. Even small steps, like switching sections or choosing from a list, create friction that disrupts focus.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              Over a full schedule, these moments add up. Clinicians end each session with a clear understanding of what occurred, yet still have to reassemble the visit in the format the tool expects. The clarity of the encounter lives in memory while the clinician works through the mechanics of documentation. The note becomes separate from the care instead of a reflection of it.
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              The challenge is not the reasoning. It is the structure wrapped around it. When documentation requires searching, navigating, and reorganizing instead of simply capturing what unfolded, it introduces work that does not belong to the clinical encounter.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Where Technology Meets Clinical Judgment</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Technology has the potential to finally meet clinicians where they are, but it still needs guidance to understand how real care unfolds. The rhythm of a session, noticing subtle changes, choosing when to pivot, deciding what matters and what does not, comes from clinical judgment that AI cannot recreate on its own.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              What it can do is support that judgment when clinicians help shape its design.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              Without that insight, any tool risks imposing structure instead of reflecting practice. But when clinicians define the logic, the system gains the context it needs to organize information in a way that aligns with real sessions. AI is not replacing reasoning; it is capturing and structuring it so clinicians do not have to rebuild the visit later.
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              A co-built approach allows AI to:
            </p>

            <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-700">
              <li>follow the cadence of the visit without steering it</li>
              <li>highlight details clinicians identify as meaningful</li>
              <li>reinforce defensible reasoning without guessing at intent</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-8">
              The strength lies in the partnership. Clinicians carry the insight, and AI handles the mechanics. When the two work together, documentation begins to match the way care actually happens, with the clinical thinking firmly in the clinician's hands.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">When Documentation Follows the Flow of Care</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              When a system is built to follow the visit instead of directing it, the daily challenge of documentation starts to look different. The note grows from what actually happens in the session as you ask questions, guide movement, adjust cues, check progress, and listen to how symptoms change. It becomes a record of the work itself, not something that has to be recreated later.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              This approach does more than capture details in the moment. It keeps clinicians present, reduces the mental load of holding information for later, and allows the note to take shape in a way that reflects the true course of care. When the structure supports the natural rhythm of the visit, clinicians regain time, patients receive fuller attention, and <Link to="/blogs/documentation-physical-therapy" className="text-emerald-600 hover:text-emerald-700 underline">documentation becomes a clear extension of the work</Link> rather than an additional burden.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Setting the Direction for Documentation</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              The next era of documentation will not be defined by faster ways to complete old workflows. It will be defined by systems that understand clinical work as it happens. Care is dynamic, shaped by what the patient shows in the moment, and the tools that support clinicians need to be able to move with that reality.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              AI can help drive this transformation, but only if guided by the people who deliver care. Technology on its own cannot understand relevance, nuance, or the clinical reasoning that connects one decision to the next. That insight has to come from clinicians, which is why the most meaningful progress will come from solutions built in partnership with them. This belief guides <Link to="/blogs/ai-scribes-healthcare-notation-physical-therapy-future" className="text-emerald-600 hover:text-emerald-700 underline">the development of Notation</Link> and anchors its focus on real practice rather than a rigid structure.
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              When documentation follows the visit instead of the form, the workload begins to rebalance. Clinicians stay engaged in the session, notes reflect what actually happened, and time lost to reconstruction at the end of the day finally starts to return to the clinician. This is the direction the field is moving toward, and the systems that embrace it will define the future of documentation.
            </p>
          </article>

          {/* Try Notation CTA Banner */}
          <div className="mt-16 p-8 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg border border-emerald-100">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Experience Documentation That Follows Your Visit?</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                👉 Try Notation for your next evaluation — no strings attached.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://notation.fownd.care/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors inline-flex items-center justify-center"
                >
                  Try Notation
                </a>
                <Link
                  to="/demo"
                  className="bg-white text-emerald-600 px-8 py-3 rounded-lg font-semibold border-2 border-emerald-600 hover:bg-emerald-50 transition-colors inline-flex items-center justify-center"
                >
                  Watch Demo
                </Link>
              </div>
            </div>
          </div>

          {/* Back button at bottom */}
          <div className="mt-12">
            <Link
              to="/blogs"
              className="inline-flex items-center px-6 py-3 border border-gray-800 rounded-full text-gray-800 hover:bg-darkGreen hover:text-white transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blogs
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default FutureOfDocumentation;
